<template>
	<AppDialog
		closeTop="-30"
		width="625"
		height="700"
		closeRight="-2"
		disable-max-width
		ref="dialog_container"
		:dialogName="'AnalyticsEventTrackingSettingsDialog'"
	>
		<div class="flex flex-col h-[700px] justify-between p-[30px]">
			<div class="w-[625px] flex flex-col items-start justify-start">
				<div class="flex flex-row align-center">
					<AnemicButton v-if="currentConnection" class="back-button mr-[10px]" width="25" height="25" @click="goBack" fab x-small>
						<AppIcon name="back" />
					</AnemicButton>
					<div class="flex flex-col">
						<div class="text-[22px] font-bold">Add tracking destination</div>
					</div>
				</div>
				<div class="text-[14px] font-normal">
					Send behavior events to your analytics, ads, and marketing automation platforms with a visual event mapper.
				</div>
				<div class="text-[14px] font-semibold mt-[25px]">Space</div>
				<div class="text-[#26262680] text-[12px] font-normal">
					The space in which the destination will be saved. Space creators can send events to the destination
				</div>
				<ProjectSelectDataProvider class="w-full" v-model="newConfiguration.project" :permission="VIEW_PROJECTS_PERMISSION" />
				<div class="text-[14px] font-semibold mt-[15px]">Destination</div>
				<div class="text-[#26262680] text-[12px] font-normal">Choose a destination where events will be sent</div>
				<template>
					<ServiceSelect
						class="w-full mt-[5px]"
						:selectedProviderId="newConfiguration.analytics_provider"
						@onProviderSelected="(provider) => (newConfiguration.analytics_provider = provider)"
					/>
				</template>
				<div class="text-[14px] font-semibold mt-[15px]">Destination name</div>
				<div class="text-[#26262680] text-[12px] font-normal">Give this destination a name you'll recognize</div>
				<AppTextField
					class="w-full mt-[5px]"
					v-model="newConfiguration.title"
					outlined
					hide-details
					placeholder="Bill's Google Analytics"
				/>
				<h3 class="font-semibold">{{ pixelIdName }}</h3>
				<div v-if="analyticsProvider" class="text-[#26262680] text-[12px] font-normal underline cursor-pointer" @click="openDocs">
					Where do I find my {{ pixelIdName }}?
				</div>
				<div v-else>
					<h5 class="text-[#26262680]">
						For example: Pixel ID for Meta Ads, see unique identifiers and how to get them
						<a class="text-[#646E87] text-[12px] font-normal" @click="openDocs">here</a>
					</h5>
				</div>
				<AppTextField class="w-full mt-[5px]" v-model="newConfiguration.identifier" outlined hide-details :placeholder="pixelPlaceholder" />
				<h3 v-if="showRegionPicker" class="font-semibold">Data center region</h3>
				<template v-if="showRegionPicker">
					<RegionSelect
						class="w-full mt-[5px]"
						:selectedRegion="selectedRegion"
						@on-region-selected="(region) => (newConfiguration.additional_data.region = region)"
					/>
				</template>
			</div>
			<div class="dialog-content flex flex-col items-center justify-end">
				<PrimaryButton class="text-[14px] font-normal w-[120px]" @click="saveConnection" :loading="isLoading" :disabled="isDisabled"
					>Save</PrimaryButton
				>
			</div>
		</div>
	</AppDialog>
</template>

<script>
import { ProjectPermissions } from '@ply-io/ply-front-shared-utilities';

import ProjectSelectDataProvider from '@/components/Project/ProjectSelect/ProjectSelectDataProvider.vue';
import RegionSelect from '@/components/dialogs/AnalyticsEventTrackingSettingsDialog/RegionSelect';
import ServiceSelect from '@/components/dialogs/AnalyticsEventTrackingSettingsDialog/ServiceSelect';
import { getProviderIdName } from '@/components/dialogs/AnalyticsEventTrackingSettingsDialog/provider_id_names';
import AppDialog from '@/components/dialogs/AppDialog/AppDialog';
import AppDialogManagerService from '@/components/dialogs/AppDialogManager/app-dialog-manager-service';
import {
	fetchAnalyticsProviders,
	addAnalyticsConfiguration,
	updateAnalyticsConfiguration,
} from '@/store/modules/analyticsProvider/actions';
import { useAnalyticsProvidersArray } from '@/store/modules/analyticsProvider/getters';
import { setDocsOpen } from '@/store/modules/onboarding/actions';
import { selectedProjectId as useSelectedProjectId } from '@/store/modules/selectedProject/getters';
import { mapActionsShorthand, mapGettersShorthand } from '@/store/utils/mappers';
const ANALYTICS_EVENTS_DOC =
	'https://docs.ply.io/build-features/integrations/analytics-event-tracking#get-your-destinations-unique-identifier';

const REGION_REQUIRED_PROVIDERS = ['customerio'];

export default {
	name: 'AnalyticsEventTrackingSettingsDialog',
	props: { currentConnection: { type: Object }, dialogPromiseHandlers: { type: Object } },
	components: {
		AppDialog,
		ServiceSelect,
		ProjectSelectDataProvider,
		RegionSelect,
	},
	computed: {
		...mapGettersShorthand({ useSelectedProjectId, useAnalyticsProvidersArray }),
		isEditMode() {
			if (!this.currentConnection) return false;
			return !!this.currentConnection.id;
		},
		isDisabled() {
			return (
				!this.newConfiguration.analytics_provider ||
				!this.newConfiguration.title ||
				!this.newConfiguration.project ||
				!this.newConfiguration.identifier
			);
		},
		analyticsProvider() {
			return this.newConfiguration.analytics_provider;
		},
		analyticsSlug() {
			return this.useAnalyticsProvidersArray.find((provider) => provider.id === this.analyticsProvider)?.slug;
		},
		pixelIdName() {
			if (this.analyticsProvider) {
				return getProviderIdName(this.analyticsSlug);
			} else {
				return 'Unique identifier';
			}
		},
		pixelPlaceholder() {
			return `Enter ${this.pixelIdName}`;
		},
		showRegionPicker() {
			return REGION_REQUIRED_PROVIDERS.includes(this.analyticsSlug);
		},
		selectedRegion() {
			return this.newConfiguration?.additional_data?.region;
		},
	},
	methods: {
		...mapActionsShorthand({ fetchAnalyticsProviders, addAnalyticsConfiguration, updateAnalyticsConfiguration, setDocsOpen }),
		close(result) {
			if (result) {
				const itemToReturn = { option: result.title, value: result.id, provider: result.analytics_provider };
				this.dialogPromiseHandlers.resolve(itemToReturn);
			} else {
				this.dialogPromiseHandlers.resolve();
			}
			AppDialogManagerService.closeCurrentDialog();
		},
		openEventDialog() {
			AppDialogManagerService.openAnalyticsEventTrackingItemDialog({ pixelConfiguration: this.newConfiguration });
		},
		async saveConnection() {
			try {
				this.isLoading = true;
				if (this.isEditMode) {
					this.newConfiguration = await this.updateAnalyticsConfiguration({
						analyticsConfiguration: this.newConfiguration,
						id: this.currentConnection.id,
					});
				} else {
					this.newConfiguration = await this.addAnalyticsConfiguration({
						analyticsConfiguration: this.newConfiguration,
					});
				}
				this.$toast.success('Destination saved');
				this.close(this.newConfiguration);
				this.openEventDialog();
			} catch (error) {
				this.$toast.error(error.message);
			} finally {
				this.isLoading = false;
			}
		},
		openDocs() {
			this.setDocsOpen(ANALYTICS_EVENTS_DOC);
		},
		goBack() {
			this.close();
			if (this.currentConnection?.id) {
				AppDialogManagerService.openAnalyticsEventTrackingItemDialog({ pixelConfiguration: this.currentConnection });
			}
		},
	},
	async created() {
		this.isLoading = true;
		try {
			await this.fetchAnalyticsProviders();
		} catch (error) {
			this.$toast.error('Error while loading analytics providers, please try again later');
		} finally {
			this.isLoading = false;
		}
		if (this.currentConnection && this.currentConnection.project) {
			this.newConfiguration.project = Number(this.currentConnection.project);
		} else {
			this.newConfiguration.project = isNaN(this.useSelectedProjectId) ? this.useSelectedProjectId : Number(this.useSelectedProjectId);
		}
		if (this?.currentConnection?.actionKey) {
			const key = this.currentConnection.actionKey;
			const serviceSlugSnake = key.substring(12);
			const serviceSlug = serviceSlugSnake.replace(/_/g, '-');
			const providerId = this.useAnalyticsProvidersArray.find((provider) => provider.slug === serviceSlug)?.id;
			this.newConfiguration.analytics_provider = providerId || null;
		}
	},
	data() {
		return {
			isLoading: false,
			newConfiguration: {
				analytics_provider: this.currentConnection?.analytics_provider || null,
				identifier: this.currentConnection?.identifier || '',
				title: this.currentConnection?.title || '',
				project: undefined,
				additional_data: this.currentConnection?.additional_data || {},
			},
			VIEW_PROJECTS_PERMISSION: ProjectPermissions.ViewProject,
		};
	},
};
</script>

<style scoped lang="scss">
.description-text {
	font-size: 14px;
	text-align: left;
	letter-spacing: 0px;
}
::v-deep {
	.auto-complete-container {
		width: 100%;
		height: 10px;
	}
	.v-icon.fas.fa-caret-down::before {
		content: '';
		color: #9a9fa8;
		font-size: 18px;
	}
	a {
		color: #646e87 !important;
	}
}
</style>
